import JSZip from 'jszip';

const RELATIONSHIPS_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const DOCUMENT_RELATIONSHIPS_NS =
  'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

const PRESERVED_PART_PREFIXES = [
  'xl/media/',
  'xl/charts/',
  'xl/embeddings/',
  'xl/drawings/',
] as const;

export interface PreserveOriginalPackagePartsOptions {
  /** Freshly serialized XLSX package that receives unsupported OOXML parts. */
  outputBuffer: ArrayBuffer;
  /** Original XLSX package from which raw drawing/media/chart parts are copied. */
  originalBuffer: ArrayBuffer;
  /** Original zero-based sheet index keyed by the output sheet name. */
  sheetOriginalIndices: ReadonlyMap<string, number>;
  /** Output workbook sheet names in workbook order. */
  outputSheetNames: readonly string[];
}

interface XmlRelationship {
  id: string;
  type: string;
  target: string;
  tag: string;
}

function attributes(tag: string): Map<string, string> {
  const result = new Map<string, string>();
  const re = /([:\w.-]+)\s*=\s*(["'])(.*?)\2/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(tag)) !== null) result.set(match[1], match[3]);
  return result;
}

function relationshipTags(xml: string): XmlRelationship[] {
  const tags = xml.match(/<Relationship\b[^>]*\/?\s*>/gi) ?? [];
  return tags.flatMap((tag) => {
    const attrs = attributes(tag);
    const id = attrs.get('Id');
    const type = attrs.get('Type');
    const target = attrs.get('Target');
    return id && type && target ? [{ id, type, target, tag }] : [];
  });
}

function normalizeZipPath(path: string): string {
  const parts: string[] = [];
  for (const part of path.replace(/^\/+/, '').split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') parts.pop();
    else parts.push(part);
  }
  return parts.join('/');
}

function resolveTarget(partPath: string, target: string): string {
  if (target.startsWith('/')) return normalizeZipPath(target);
  const slash = partPath.lastIndexOf('/');
  const base = slash === -1 ? '' : partPath.slice(0, slash + 1);
  return normalizeZipPath(`${base}${target}`);
}

function relativeTarget(fromPartPath: string, absoluteTarget: string): string {
  const from = normalizeZipPath(fromPartPath).split('/');
  from.pop();
  const to = normalizeZipPath(absoluteTarget).split('/');
  let common = 0;
  while (common < from.length && common < to.length && from[common] === to[common]) common += 1;
  return [...Array(from.length - common).fill('..'), ...to.slice(common)].join('/');
}

function relationshipsPathForPart(partPath: string): string {
  const slash = partPath.lastIndexOf('/');
  const dir = slash === -1 ? '' : partPath.slice(0, slash);
  const file = slash === -1 ? partPath : partPath.slice(slash + 1);
  return `${dir ? `${dir}/` : ''}_rels/${file}.rels`;
}

async function worksheetPaths(zip: JSZip): Promise<string[]> {
  const workbook = zip.file('xl/workbook.xml');
  const workbookRels = zip.file('xl/_rels/workbook.xml.rels');
  if (!workbook || !workbookRels) return [];

  const [workbookXml, relsXml] = await Promise.all([
    workbook.async('text'),
    workbookRels.async('text'),
  ]);
  const targetById = new Map(
    relationshipTags(relsXml)
      .filter((rel) => /\/worksheet$/i.test(rel.type))
      .map((rel) => [rel.id, resolveTarget('xl/workbook.xml', rel.target)]),
  );

  return (workbookXml.match(/<sheet\b[^>]*\/?\s*>/gi) ?? []).flatMap((tag) => {
    const attrs = attributes(tag);
    const relationshipId = attrs.get('r:id');
    const target = relationshipId ? targetById.get(relationshipId) : undefined;
    return target ? [target] : [];
  });
}

function worksheetPath(paths: readonly string[], index: number): string {
  return paths[index] ?? `xl/worksheets/sheet${index + 1}.xml`;
}

function relationshipKind(type: string): 'drawing' | 'vmlDrawing' | null {
  if (/\/vmlDrawing$/i.test(type)) return 'vmlDrawing';
  if (/\/drawing$/i.test(type)) return 'drawing';
  return null;
}

function nextRelationshipId(xml: string): () => string {
  const used = new Set(relationshipTags(xml).map((rel) => rel.id));
  let next = 1;
  return () => {
    while (used.has(`rId${next}`)) next += 1;
    const id = `rId${next}`;
    used.add(id);
    next += 1;
    return id;
  };
}

function replaceRelationshipAttribute(tag: string, name: 'Id' | 'Target', value: string): string {
  const re = new RegExp(`\\b${name}\\s*=\\s*(["'])[^"']*\\1`);
  return tag.replace(re, `${name}="${value}"`);
}

function appendRelationships(xml: string, tags: readonly string[]): string {
  if (tags.length === 0) return xml;
  const body = tags.join('');
  if (/<\/Relationships\s*>/i.test(xml)) {
    return xml.replace(/<\/Relationships\s*>/i, `${body}</Relationships>`);
  }
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="${RELATIONSHIPS_NS}">${body}</Relationships>`;
}

function ensureDocumentRelationshipsNamespace(xml: string): string {
  if (/xmlns:r\s*=/.test(xml)) return xml;
  return xml.replace(/<worksheet\b/, `<worksheet xmlns:r="${DOCUMENT_RELATIONSHIPS_NS}"`);
}

function appendWorksheetReferences(
  xml: string,
  references: ReadonlyArray<{ kind: 'drawing' | 'vmlDrawing'; id: string }>,
): string {
  const additions: string[] = [];
  for (const reference of references) {
    if (reference.kind === 'drawing' && !/<drawing\b/i.test(xml)) {
      additions.push(`<drawing r:id="${reference.id}"/>`);
    }
    if (reference.kind === 'vmlDrawing' && !/<legacyDrawing\b/i.test(xml)) {
      additions.push(`<legacyDrawing r:id="${reference.id}"/>`);
    }
  }
  if (additions.length === 0) return xml;

  let next = ensureDocumentRelationshipsNamespace(xml);
  const anchors = [
    '<picture', '<oleObjects', '<controls', '<webPublishItems', '<tableParts', '<extLst',
    '</worksheet',
  ];
  let insertAt = -1;
  for (const anchor of anchors) {
    const candidate = next.indexOf(anchor);
    if (candidate !== -1 && (insertAt === -1 || candidate < insertAt)) insertAt = candidate;
  }
  if (insertAt === -1) return next;
  return next.slice(0, insertAt) + additions.join('') + next.slice(insertAt);
}

async function copyPreservedParts(original: JSZip, output: JSZip): Promise<string[]> {
  const paths = Object.keys(original.files).filter(
    (path) =>
      !original.files[path].dir &&
      PRESERVED_PART_PREFIXES.some((prefix) => path.startsWith(prefix)) &&
      // The freshly serialized package is authoritative. In particular, the
      // comment writer may already have created a VML part at the same path as
      // the source package. Never replace generated/current output bytes with
      // an original sidecar part.
      !output.file(path),
  );
  await Promise.all(paths.map(async (path) => output.file(path, await original.files[path].async('arraybuffer'))));
  return paths;
}

function appendContentTypeEntries(
  outputXml: string,
  originalXml: string,
  copiedPaths: readonly string[],
): string {
  const copiedPartNames = new Set(copiedPaths.map((path) => path.toLowerCase()));
  const copiedExtensions = new Set(
    copiedPaths.flatMap((path) => {
      const match = /\.([^./]+)$/.exec(path);
      return match ? [match[1].toLowerCase()] : [];
    }),
  );
  const existingDefaults = new Set<string>();
  const existingOverrides = new Set<string>();
  for (const tag of outputXml.match(/<(?:Default|Override)\b[^>]*\/?\s*>/gi) ?? []) {
    const attrs = attributes(tag);
    const extension = attrs.get('Extension')?.toLowerCase();
    const partName = attrs.get('PartName');
    if (extension) existingDefaults.add(extension);
    if (partName) existingOverrides.add(partName.toLowerCase());
  }

  const additions: string[] = [];
  for (const tag of originalXml.match(/<(?:Default|Override)\b[^>]*\/?\s*>/gi) ?? []) {
    const attrs = attributes(tag);
    const extension = attrs.get('Extension')?.toLowerCase();
    const partName = attrs.get('PartName');
    if (extension && copiedExtensions.has(extension) && !existingDefaults.has(extension)) {
      additions.push(tag);
      existingDefaults.add(extension);
      continue;
    }
    const normalizedPartName = partName?.replace(/^\//, '').toLowerCase();
    if (
      partName &&
      normalizedPartName &&
      copiedPartNames.has(normalizedPartName) &&
      !existingOverrides.has(partName.toLowerCase())
    ) {
      additions.push(tag);
      existingOverrides.add(partName.toLowerCase());
    }
  }
  if (additions.length === 0) return outputXml;
  return outputXml.replace(/<\/Types\s*>/i, `${additions.join('')}</Types>`);
}

/**
 * Restores raw OOXML parts that SheetJS does not serialize (images, drawings,
 * charts and embedded chart data) into a freshly written XLSX package.
 *
 * This API deliberately accepts and returns ArrayBuffer so the engine remains
 * independent from Blob, FileReader and browser DOM implementations.
 */
export async function preserveOriginalPackageParts({
  outputBuffer,
  originalBuffer,
  sheetOriginalIndices,
  outputSheetNames,
}: PreserveOriginalPackagePartsOptions): Promise<ArrayBuffer> {
  const [originalZip, outputZip] = await Promise.all([
    JSZip.loadAsync(originalBuffer),
    JSZip.loadAsync(outputBuffer),
  ]);

  const copiedPaths = await copyPreservedParts(originalZip, outputZip);
  if (copiedPaths.length === 0) return outputBuffer;

  const [originalWorksheetPaths, outputWorksheetPaths] = await Promise.all([
    worksheetPaths(originalZip),
    worksheetPaths(outputZip),
  ]);
  const outputOwnedPartPaths = new Set(
    Object.keys(outputZip.files).filter(
      (path) => !outputZip.files[path].dir && !copiedPaths.includes(path),
    ),
  );
  const attachedCopiedTargets = new Set<string>();

  for (let outputIndex = 0; outputIndex < outputSheetNames.length; outputIndex += 1) {
    const originalIndex = sheetOriginalIndices.get(outputSheetNames[outputIndex]);
    if (originalIndex === undefined) continue;

    const originalSheetPath = worksheetPath(originalWorksheetPaths, originalIndex);
    const outputSheetPath = worksheetPath(outputWorksheetPaths, outputIndex);
    const originalRelsFile = originalZip.file(relationshipsPathForPart(originalSheetPath));
    const outputSheetFile = outputZip.file(outputSheetPath);
    if (!originalRelsFile || !outputSheetFile) continue;

    const preservedRelationships = relationshipTags(await originalRelsFile.async('text'))
      .flatMap((relationship) => {
        const kind = relationshipKind(relationship.type);
        return kind ? [{ ...relationship, kind }] : [];
      });
    if (preservedRelationships.length === 0) continue;

    const outputRelsPath = relationshipsPathForPart(outputSheetPath);
    const outputRelsFile = outputZip.file(outputRelsPath);
    const outputRelsXml = outputRelsFile
      ? await outputRelsFile.async('text')
      : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<Relationships xmlns="${RELATIONSHIPS_NS}"></Relationships>`;
    const outputSheetXml = await outputSheetFile.async('text');
    const outputOwnedKinds = new Set<'drawing' | 'vmlDrawing'>(
      relationshipTags(outputRelsXml).flatMap((relationship) => {
        const kind = relationshipKind(relationship.type);
        return kind ? [kind] : [];
      }),
    );
    if (/<drawing\b/i.test(outputSheetXml)) outputOwnedKinds.add('drawing');
    if (/<legacyDrawing\b/i.test(outputSheetXml)) outputOwnedKinds.add('vmlDrawing');

    const allocateId = nextRelationshipId(outputRelsXml);
    const worksheetReferences: Array<{ kind: 'drawing' | 'vmlDrawing'; id: string }> = [];
    const relationshipXml: string[] = [];
    for (const relationship of preservedRelationships) {
      // A worksheet can own one DrawingML drawing and one legacy VML drawing.
      // If the fresh output already owns that kind (notably a note VML emitted
      // by serializeWorkbook), it wins and the original relationship/part must
      // not be reattached as an orphan duplicate.
      if (outputOwnedKinds.has(relationship.kind)) continue;

      const absoluteTarget = resolveTarget(originalSheetPath, relationship.target);
      // A target that existed before preservation is generated/current output.
      // Since copyPreservedParts intentionally did not overwrite it, do not
      // redirect an original worksheet relationship to those unrelated bytes.
      if (outputOwnedPartPaths.has(absoluteTarget)) continue;
      if (!outputZip.file(absoluteTarget)) continue;

      const id = allocateId();
      const target = relativeTarget(outputSheetPath, absoluteTarget);
      worksheetReferences.push({ kind: relationship.kind, id });
      relationshipXml.push(replaceRelationshipAttribute(
        replaceRelationshipAttribute(relationship.tag, 'Id', id),
        'Target',
        target,
      ));
      outputOwnedKinds.add(relationship.kind);
      attachedCopiedTargets.add(absoluteTarget);
    }

    if (relationshipXml.length === 0) continue;

    outputZip.file(outputRelsPath, appendRelationships(outputRelsXml, relationshipXml));
    outputZip.file(
      outputSheetPath,
      appendWorksheetReferences(outputSheetXml, worksheetReferences),
    );
  }

  // VML drawings are direct worksheet-owned parts. If a generated note VML
  // already won for every mapped output sheet, remove an original VML (and its
  // optional relationships part) that was copied but never attached.
  for (const path of copiedPaths) {
    if (!/\.vml$/i.test(path) || attachedCopiedTargets.has(path)) continue;
    outputZip.remove(path);
    const relsPath = relationshipsPathForPart(path);
    if (copiedPaths.includes(relsPath)) outputZip.remove(relsPath);
  }
  const retainedCopiedPaths = copiedPaths.filter((path) => outputZip.file(path) !== null);

  const originalContentTypes = originalZip.file('[Content_Types].xml');
  const outputContentTypes = outputZip.file('[Content_Types].xml');
  if (originalContentTypes && outputContentTypes) {
    const [originalXml, outputXml] = await Promise.all([
      originalContentTypes.async('text'),
      outputContentTypes.async('text'),
    ]);
    outputZip.file(
      '[Content_Types].xml',
      appendContentTypeEntries(outputXml, originalXml, retainedCopiedPaths),
    );
  }

  return outputZip.generateAsync({ type: 'arraybuffer' });
}
